import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  GRADE_LEVELS,
  STUDENT_TEMPLATE_HEADERS,
  createEmptyStudent,
} from '../constants/formConfig';

const GRADE_ALIASES = {
  '3': 'Third',
  '3rd': 'Third',
  third: 'Third',
  '4': 'Fourth',
  '4th': 'Fourth',
  fourth: 'Fourth',
  '5': 'Fifth',
  '5th': 'Fifth',
  fifth: 'Fifth',
  '6': 'Sixth',
  '6th': 'Sixth',
  sixth: 'Sixth',
  '7': 'Seventh',
  '7th': 'Seventh',
  seventh: 'Seventh',
  '8': 'Eighth',
  '8th': 'Eighth',
  eighth: 'Eighth',
};

function normalizeHeader(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function normalizeGrade(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (GRADE_LEVELS.includes(raw)) return raw;
  return GRADE_ALIASES[raw.toLowerCase()] || '';
}

function getCell(row, keys) {
  const entries = Object.entries(row);
  for (const key of keys) {
    const match = entries.find(
      ([header]) => normalizeHeader(header) === normalizeHeader(key),
    );
    if (match) return match[1];
  }
  return '';
}

function splitFullName(fullName) {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

/**
 * Parse the Tests column: "Computation", "Reasoning",
 * "Reasoning, Computation", "Both", etc.
 * Also accepts legacy separate Reasoning / Computation Yes/No columns.
 */
function parseTests(row) {
  const testsCell = String(
    getCell(row, ['Tests', 'Test', 'Test(s)']),
  ).trim();

  if (testsCell) {
    const normalized = testsCell.toLowerCase();
    const reasoning =
      normalized.includes('reasoning') ||
      normalized === 'both' ||
      normalized === 'r';
    const computation =
      normalized.includes('computation') ||
      normalized === 'both' ||
      normalized === 'c';
    return { reasoning, computation };
  }

  // Legacy template: separate Yes/No columns
  const reasoningRaw = getCell(row, ['Reasoning', 'reasoning test']);
  const computationRaw = getCell(row, ['Computation', 'computation test']);
  const truthy = (value) => {
    if (typeof value === 'boolean') return value;
    const normalized = String(value ?? '')
      .trim()
      .toLowerCase();
    return ['y', 'yes', 'true', '1', 'x'].includes(normalized);
  };

  return {
    reasoning: truthy(reasoningRaw),
    computation: truthy(computationRaw),
  };
}

/** Dropdown options for the Tests column. Parser accepts "Both" as both tests. */
const TEST_OPTIONS = ['Reasoning', 'Computation', 'Both'];

/** Rows that get grade/test dropdowns pre-applied so users can add students freely. */
const TEMPLATE_VALIDATION_ROWS = 500;

function columnLetter(headerName) {
  const index = STUDENT_TEMPLATE_HEADERS.indexOf(headerName);
  return index >= 0 ? String.fromCharCode(65 + index) : null;
}

/**
 * Builds the Excel template with in-cell dropdowns for Grade Level and Tests.
 * ExcelJS is loaded on demand — SheetJS (used for parsing) can't write
 * data-validation dropdowns, and we don't want ExcelJS in the main bundle.
 */
export async function downloadStudentTemplate() {
  const { default: ExcelJS } = await import('exceljs');

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Students');

  sheet.columns = STUDENT_TEMPLATE_HEADERS.map((header) => ({
    header,
    width: Math.max(18, header.length + 6),
  }));
  sheet.getRow(1).font = { bold: true };

  // Sample row so users see the expected format.
  sheet.addRow(['Subhash', 'Sah', 'Third', 'Computation']);

  const gradeCol = columnLetter('Grade Level');
  const testsCol = columnLetter('Tests');
  const gradeList = `"${GRADE_LEVELS.join(',')}"`;
  const testsList = `"${TEST_OPTIONS.join(',')}"`;

  for (let row = 2; row <= TEMPLATE_VALIDATION_ROWS; row += 1) {
    if (gradeCol) {
      sheet.getCell(`${gradeCol}${row}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [gradeList],
        showErrorMessage: true,
        errorTitle: 'Invalid grade',
        error: 'Choose a grade from the list (Third–Eighth).',
      };
    }
    if (testsCol) {
      sheet.getCell(`${testsCol}${row}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [testsList],
        showErrorMessage: true,
        errorTitle: 'Invalid test',
        error: 'Choose Reasoning, Computation, or Both.',
      };
    }
  }

  const guide = workbook.addWorksheet('Instructions');
  guide.addRows([
    ['Column Guide'],
    ["Student's First Name", 'Required'],
    ["Student's Last Name", 'Required'],
    ['Grade Level', 'Choose from the dropdown: Third through Eighth'],
    ['Tests', 'Choose from the dropdown: Reasoning, Computation, or Both'],
  ]);
  guide.getColumn(1).width = 24;
  guide.getColumn(2).width = 54;

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    'IMC_Student_Registration_Template.xlsx',
  );
}

export function parseStudentSpreadsheet(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '', raw: false });

        if (!rows.length) {
          reject(new Error('The spreadsheet appears to be empty.'));
          return;
        }

        const students = [];
        const errors = [];

        rows.forEach((row, index) => {
          const rowNumber = index + 2;

          let firstName = String(
            getCell(row, [
              "Student's First Name",
              'Students First Name',
              'First Name',
              'first name',
              'firstname',
            ]),
          ).trim();
          let lastName = String(
            getCell(row, [
              "Student's Last Name",
              'Students Last Name',
              'Last Name',
              'last name',
              'lastname',
            ]),
          ).trim();

          const fullName = String(
            getCell(row, ['Student Name', 'studentname', 'Full Name']),
          ).trim();

          if ((!firstName || !lastName) && fullName) {
            const split = splitFullName(fullName);
            if (!firstName) firstName = split.firstName;
            if (!lastName) lastName = split.lastName;
          }

          const gradeLevel = normalizeGrade(
            getCell(row, ['Grade Level', 'grade', 'grade level']),
          );
          const { reasoning, computation } = parseTests(row);

          const emptyRow =
            !firstName &&
            !lastName &&
            !fullName &&
            !gradeLevel &&
            !reasoning &&
            !computation;

          if (emptyRow) return;

          if (!firstName || !lastName) {
            errors.push(
              `Row ${rowNumber}: Student's First Name and Last Name are required.`,
            );
          }
          if (!gradeLevel) {
            errors.push(
              `Row ${rowNumber}: Grade Level must be Third through Eighth.`,
            );
          }
          if (!reasoning && !computation) {
            errors.push(
              `Row ${rowNumber}: Tests must include Reasoning and/or Computation.`,
            );
          }

          const student = createEmptyStudent();
          student.firstName = firstName;
          student.lastName = lastName;
          student.gradeLevel = gradeLevel;
          student.tests = { reasoning, computation };
          students.push(student);
        });

        if (!students.length) {
          reject(new Error('No valid student rows were found in the file.'));
          return;
        }

        resolve({
          students,
          errors,
        });
      } catch (error) {
        reject(
          new Error('Unable to read that spreadsheet. Please use the template.'),
        );
      }
    };

    reader.onerror = () =>
      reject(new Error('Failed to read the selected file.'));
    reader.readAsArrayBuffer(file);
  });
}
